import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, MoveRight } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errMsg, fmtDate, STATI, initials } from "@/lib/api";
import { PriorityBadge } from "./TaskBadges";
import { TaskFormDialog } from "./TaskFormDialog";
import { ReservedLine } from "@/components/client/ReservedLine";
import { CommentBadge } from "./CommentsThread";

const COL_ACCENT = { da_fare: "#F59E0B", in_corso: "#2B9BE8", completata: "#10B981" };
const byOrder = (a, b) => (a.ordine || 0) - (b.ordine || 0) || (a.scadenza || "9999").localeCompare(b.scadenza || "9999");

const Card = ({ t, showClient, users, onOpen, onDragStart, onDragOver, dragging, onMove }) => {
  const u = users.find((x) => x.id === t.assignee_id);
  return (
    <div draggable onDragStart={(e) => onDragStart(e, t)} onDragOver={(e) => onDragOver(e, t)} onClick={() => onOpen(t)}
      className={`cursor-grab active:cursor-grabbing rounded-xl border border-[#5FB3EA] bg-[#A6DAFC] p-3 shadow-sm hover:shadow-md transition-[box-shadow,opacity,transform] ${dragging ? "opacity-40" : ""}`}
      data-testid={`board-card-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`font-bold text-sm text-[#0A1128] ${t.stato === "completata" ? "line-through opacity-70" : ""}`}>{t.titolo}</div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button className="lg:hidden shrink-0 h-9 px-2.5 rounded-full border border-[#5FB3EA] bg-[#8FD0FB] text-[11px] font-bold text-[#0A1128] inline-flex items-center gap-1" data-testid={`board-card-move-${t.id}`}><MoveRight className="h-3.5 w-3.5" />Sposta</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel>Sposta in…</DropdownMenuLabel>
            {STATI.filter((s) => s.value !== t.stato).map((s) => (
              <DropdownMenuItem key={s.value} onSelect={() => onMove(t, s.value)} className="min-h-[44px]" data-testid={`board-card-move-${t.id}-${s.value}`}>{s.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {showClient && <div className="text-xs font-semibold text-slate-500 mt-0.5 truncate">{t.client_nome}</div>}
      <ReservedLine clientId={t.client_id} className="mt-1" testid={`task-card-reserved-${t.id}`} />
      <div className="flex items-center justify-between gap-2 mt-2.5">
        <div className="flex items-center gap-2">
          <PriorityBadge value={t.priorita} />
          <CommentBadge n={t.commenti} testid={`board-card-comments-${t.id}`} />
        </div>
        <div className="flex items-center gap-2">
          {t.scadenza && <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${t.scaduta ? "text-[#C8102E]" : "text-slate-600"}`} data-testid={`board-card-due-${t.id}`}><CalendarClock className="h-3 w-3" />{fmtDate(t.scadenza)}</span>}
          <span className="h-6 w-6 rounded-full bg-[#0A1128] text-white text-[10px] font-bold flex items-center justify-center" title={t.assignee_nome || "Non assegnata"}>
            {u ? initials(u.nome, u.cognome) : "–"}
          </span>
        </div>
      </div>
    </div>
  );
};

export const TaskBoard = ({ tasks, onChanged, showClient = true, users = [], clients = [], clientId }) => {
  const [items, setItems] = useState(tasks || []);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [edit, setEdit] = useState(null);
  useEffect(() => setItems(tasks || []), [tasks]);

  const colItems = (stato) => items.filter((t) => t.stato === stato).sort(byOrder);

  const onCardOver = (e, t) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const list = colItems(t.stato).filter((x) => x.id !== drag?.id);
    const i = list.findIndex((x) => x.id === t.id);
    setOver({ stato: t.stato, index: e.clientY > r.top + r.height / 2 ? i + 1 : i });
  };

  const drop = async (stato) => {
    if (!drag) return;
    const target = over?.stato === stato ? over.index : colItems(stato).filter((x) => x.id !== drag.id).length;
    const list = colItems(stato).filter((x) => x.id !== drag.id);
    const prev = list[target - 1], next = list[target];
    const tie = prev && next && (prev.ordine || 0) === (next.ordine || 0);
    const before = items;
    let updates;
    if (tie || (!prev && !next)) {
      const seq = [...list.slice(0, target), drag, ...list.slice(target)];
      updates = seq.map((x, i) => ({ id: x.id, stato, ordine: i + 1 }));
    } else {
      const ordine = prev && next ? ((prev.ordine || 0) + (next.ordine || 0)) / 2 : prev ? (prev.ordine || 0) + 1 : (next.ordine || 0) - 1;
      updates = [{ id: drag.id, stato, ordine }];
    }
    const map = Object.fromEntries(updates.map((u) => [u.id, u]));
    setItems((cur) => cur.map((x) => (map[x.id] ? { ...x, stato, ordine: map[x.id].ordine } : x)));
    setDrag(null); setOver(null);
    try {
      await Promise.all(updates.map((u) => api.patch(`/tasks/${u.id}/move`, { stato: u.stato, ordine: u.ordine })));
      if (drag.stato !== stato) toast.success("Stato aggiornato");
      onChanged?.();
    } catch (e) {
      setItems(before);
      toast.error(errMsg(e, "Spostamento non riuscito"));
    }
  };

  const moveTo = async (t, stato) => {
    const last = colItems(stato).slice(-1)[0];
    const ordine = (last?.ordine || 0) + 1;
    const before = items;
    setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, stato, ordine } : x)));
    try {
      await api.patch(`/tasks/${t.id}/move`, { stato, ordine });
      toast.success("Stato aggiornato");
      onChanged?.();
    } catch (e) {
      setItems(before);
      toast.error(errMsg(e, "Spostamento non riuscito"));
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="task-board">
        {STATI.map((s) => {
          const list = colItems(s.value);
          return (
            <div key={s.value} onDragOver={(e) => { e.preventDefault(); if (!list.length || over?.stato !== s.value) setOver((o) => (o?.stato === s.value ? o : { stato: s.value, index: list.length })); }}
              onDrop={(e) => { e.preventDefault(); drop(s.value); }}
              className={`rounded-2xl border bg-[#8FD0FB] p-3 md:min-h-[420px] transition-colors ${over?.stato === s.value && drag ? "border-[#0A1128] border-dashed" : "border-[#5FB3EA]"}`}
              data-testid={`board-column-${s.value}`}>
              <div className="flex items-center justify-between px-1 pb-3">
                <div className="flex items-center gap-2 font-display font-extrabold text-[#0A1128]"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COL_ACCENT[s.value] }} />{s.label}</div>
                <span className="text-xs font-bold rounded-full bg-[#A6DAFC] px-2 py-0.5" data-testid={`board-count-${s.value}`}>{list.length}</span>
              </div>
              <div className="space-y-2.5">
                {list.map((t) => (
                  <div key={t.id}>
                    {over?.stato === s.value && drag && list.filter((x) => x.id !== drag.id).indexOf(t) === over.index && <div className="h-1 rounded bg-[#E8173A] mb-2" />}
                    <Card t={t} showClient={showClient} users={users} onOpen={setEdit} dragging={drag?.id === t.id}
                      onDragStart={(e, x) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", x.id); setDrag(x); }} onDragOver={onCardOver} onMove={moveTo} />
                  </div>
                ))}
                {!list.length && <div className="text-xs text-slate-500 text-center py-8 border-2 border-dashed border-[#5FB3EA] rounded-xl">Trascina qui una task</div>}
              </div>
            </div>
          );
        })}
      </div>
      <TaskFormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} task={edit} users={users} clients={clients} clientId={clientId} onSaved={onChanged} onCommentsChange={onChanged} />
    </>
  );
};
